import React, { useEffect, useRef, useState } from "react";
import { Box, FormControl, FormHelperText, FormLabel, TextField } from "@mui/material";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

type PlaceLike = {
  formattedAddress?: string;
  location?: {
    lat?: () => number;
    lng?: () => number;
  };
  fetchFields?: (request: { fields: string[] }) => Promise<void>;
};

type PlacePredictionLike = {
  toPlace?: () => PlaceLike;
};

type PlaceSelectEventLike = Event & {
  place?: PlaceLike;
  placePrediction?: PlacePredictionLike;
};

type PlaceAutocompleteElementLike = HTMLElement & {
  value: string;
  placeholder: string | null;
  disabled: boolean;
  includedRegionCodes: string[] | null;
  requestedLanguage: string | null;
  requestedRegion: string | null;
};

type PlacesLibraryLike = {
  PlaceAutocompleteElement?: new (options: {
    value?: string | null;
    placeholder?: string | null;
    includedRegionCodes?: string[] | null;
    requestedLanguage?: string | null;
    requestedRegion?: string | null;
    disabled?: boolean | null;
  }) => PlaceAutocompleteElementLike;
};

type GoogleGlobalLike = {
  maps?: {
    importLibrary?: (name: string) => Promise<unknown>;
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

type LocationValue = {
  address: string;
  lat: number;
  lng: number;
};

type Props = {
  value: LocationValue;
  onChange: (value: LocationValue) => void;
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  disabled?: boolean;
  showMap?: boolean;
  mapToggle?: React.ReactNode;
};

type AdvancedMarkerElementLike = {
  map: google.maps.Map | null;
  position: google.maps.LatLngLiteral | null;
};

const GOOGLE_MAP_LIBRARIES: "places"[] = ["places"];

const DEFAULT_CENTER_MX = { lat: 19.432608, lng: -99.133209 };

const ControlledPlaceAutocompleteInner: React.FC<Props & { apiKey: string; mapId: string }> = ({
  value,
  onChange,
  error,
  helperText,
  onBlur,
  disabled,
  showMap,
  mapToggle,
  apiKey,
  mapId,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAP_LIBRARIES,
    version: "weekly",
    language: "es",
    region: "MX",
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const elementRef = useRef<PlaceAutocompleteElementLike | null>(null);
  const isFocusedRef = useRef<boolean>(false);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const lastSelectedRef = useRef<LocationValue | null>(null);
  const [internalError, setInternalError] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const advancedMarkerRef = useRef<AdvancedMarkerElementLike | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    // Ensure the suggestions dropdown is visible over MUI modals/dialogs.
    const styleId = "gmaps-pac-container-zindex";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = ".pac-container{z-index:10000 !important;}";
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (loadError) {
      setInternalError(loadError instanceof Error ? loadError.message : String(loadError));
    }
  }, [loadError]);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || elementRef.current) return;

    let cancelled = false;
    let element: PlaceAutocompleteElementLike | null = null;

    const mount = async () => {
      const googleGlobal = (window as unknown as { google?: GoogleGlobalLike }).google;
      const importer = googleGlobal?.maps?.importLibrary;
      if (typeof importer !== "function") return;

      const lib = await importer("places");
      if (!isRecord(lib)) return;

      const maybeCtor = lib["PlaceAutocompleteElement"];
      if (typeof maybeCtor !== "function") return;

      const Ctor = maybeCtor as PlacesLibraryLike["PlaceAutocompleteElement"];
      if (!Ctor || cancelled || !containerRef.current || elementRef.current) return;

      element = new Ctor({
        placeholder: "Dirección",
        includedRegionCodes: ["mx"],
        requestedRegion: "mx",
        requestedLanguage: "es",
      });

      element.style.width = "100%";
      element.style.display = "block";
      containerRef.current.appendChild(element);
      elementRef.current = element;

      const handleFocusIn = () => {
        isFocusedRef.current = true;
      };

      const handleFocusOut = () => {
        isFocusedRef.current = false;
      };

      const handleInput = () => {
        if (!element) return;
        const nextAddress = element.value;

        // Si el usuario modificó el texto manualmente, invalida las coords para
        // obligarlo a seleccionar del listado o del mapa.
        if (lastSelectedRef.current && nextAddress !== lastSelectedRef.current.address) {
          lastSelectedRef.current = null;
        }

        if (lastSelectedRef.current && nextAddress === lastSelectedRef.current.address) {
          onChangeRef.current(lastSelectedRef.current);
          return;
        }

        const current = valueRef.current;
        const shouldClearCoords = nextAddress !== (current?.address ?? "");

        onChangeRef.current({
          address: nextAddress,
          lat: shouldClearCoords ? 0 : current?.lat ?? 0,
          lng: shouldClearCoords ? 0 : current?.lng ?? 0,
        });
      };

      const handleSelect = async (ev: Event) => {
        if (!element) return;
        const event = ev as PlaceSelectEventLike;
        const place = event.place ?? event.placePrediction?.toPlace?.();
        if (!place) return;

        // En algunos flujos, location/formattedAddress ya vienen disponibles.
        // Sólo hacemos fetchFields cuando realmente falten.
        let address = place.formattedAddress ?? element.value;
        let lat = place.location?.lat?.();
        let lng = place.location?.lng?.();

        const needsFields = !address || typeof lat !== "number" || typeof lng !== "number";
        if (needsFields) {
          try {
            await place.fetchFields?.({ fields: ["formattedAddress", "location"] });
          } catch {
            // ignore
          }

          address = place.formattedAddress ?? element.value;
          lat = place.location?.lat?.();
          lng = place.location?.lng?.();
        }

        const nextValue: LocationValue = {
          address,
          lat: typeof lat === "number" ? lat : 0,
          lng: typeof lng === "number" ? lng : 0,
        };

        lastSelectedRef.current = nextValue;

        // El wrapper evita sincronizar value mientras el input tiene foco para
        // no interrumpir el tipeo; en selección sí debemos reflejar el valor
        // inmediatamente, sin depender de un blur.
        element.value = address;

        // Algunos navegadores/widgets no repintan hasta blur; forzamos notificación.
        element.dispatchEvent(new Event("input", { bubbles: true }));

        onChangeRef.current(nextValue);
      };

      const handleGmpError = () => {
        setInternalError("Error en Autocomplete de Google Places");
      };

      element.addEventListener("input", handleInput);
      // Compat: el widget nuevo usa gmp-placeselect, pero mantenemos gmp-select.
      element.addEventListener("gmp-placeselect", handleSelect as EventListener);
      element.addEventListener("gmp-select", handleSelect as EventListener);
      element.addEventListener("gmp-error", handleGmpError as EventListener);
      element.addEventListener("focusin", handleFocusIn);
      element.addEventListener("focusout", handleFocusOut);

      // Cleanup
      return () => {
        element?.removeEventListener("input", handleInput);
        element?.removeEventListener("gmp-placeselect", handleSelect as EventListener);
        element?.removeEventListener("gmp-select", handleSelect as EventListener);
        element?.removeEventListener("gmp-error", handleGmpError as EventListener);
        element?.removeEventListener("focusin", handleFocusIn);
        element?.removeEventListener("focusout", handleFocusOut);
      };
    };

    let detach: (() => void) | undefined;
    void mount().then((cleanup) => {
      detach = cleanup;
    });

    return () => {
      cancelled = true;
      detach?.();
      element?.remove();
      elementRef.current = null;
    };
  }, [isLoaded]);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    el.disabled = Boolean(disabled);

    // No sincronizar el value mientras el usuario escribe; con Shadow DOM,
    // :focus-within puede no comportarse como esperamos.
    if (isFocusedRef.current) return;

    const nextValue = value?.address || "";
    if (el.value !== nextValue) {
      el.value = nextValue;
    }
  }, [disabled, value?.address]);

  const effectiveError = Boolean(error) || Boolean(internalError);
  const effectiveHelperText = internalError ? `Google Maps no cargó: ${internalError}` : helperText;

  const hasCoords = Boolean(value?.lat) && Boolean(value?.lng) && (value.lat !== 0 || value.lng !== 0);
  const markerLat = hasCoords ? value.lat : null;
  const markerLng = hasCoords ? value.lng : null;
  const markerPosition = markerLat !== null && markerLng !== null ? { lat: markerLat, lng: markerLng } : null;
  const mapCenter = markerPosition ?? DEFAULT_CENTER_MX;

  useEffect(() => {
    const map = mapRef.current;
    if (!isLoaded || !map) return;

    let cancelled = false;

    const teardown = () => {
      if (advancedMarkerRef.current) {
        advancedMarkerRef.current.map = null;
        advancedMarkerRef.current = null;
      }
    };

    if (markerLat === null || markerLng === null) {
      teardown();
      return;
    }

    // Advanced markers requieren un Map ID válido. Si no hay mapId, no intentamos
    // crear el AdvancedMarkerElement para evitar el warning de consola.
    if (!mapId) {
      teardown();
      return;
    }

    const nextPosition = { lat: markerLat, lng: markerLng };

    const ensureMarker = async () => {
      try {
        const googleGlobal = (window as unknown as { google?: GoogleGlobalLike }).google;
        const importer = googleGlobal?.maps?.importLibrary;
        if (typeof importer !== "function") return;

        const markerLib = await importer("marker");
        if (cancelled) return;
        if (!isRecord(markerLib)) return;

        const AdvancedMarkerElement = markerLib["AdvancedMarkerElement"] as
          | (new (opts: { map: google.maps.Map; position: google.maps.LatLngLiteral }) => AdvancedMarkerElementLike)
          | undefined;

        if (typeof AdvancedMarkerElement !== "function") return;

        if (!advancedMarkerRef.current) {
          advancedMarkerRef.current = new AdvancedMarkerElement({ map, position: nextPosition });
          return;
        }

        advancedMarkerRef.current.map = map;
        advancedMarkerRef.current.position = nextPosition;
      } catch {
        // Si falla la carga del marker library, evitamos crashear y simplemente no renderizamos marcador.
      }
    };

    void ensureMarker();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, markerLat, markerLng, mapId]);

  const handleMapClick = (ev: google.maps.MapMouseEvent) => {
    const lat = ev.latLng?.lat();
    const lng = ev.latLng?.lng();
    if (typeof lat !== "number" || typeof lng !== "number") return;

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      const formatted = results?.[0]?.formatted_address;
      if (status === "OK" && formatted) {
        onChange({ address: formatted, lat, lng });
        return;
      }

      // Fallback: guarda coords aunque no haya texto.
      onChange({ address: value?.address || "", lat, lng });
    });
  };

  return (
    <FormControl fullWidth error={effectiveError} disabled={Boolean(disabled)}>
      <FormLabel>Dirección</FormLabel>
      <Box sx={{ mt: 1, "& > *": { width: "100%" } }} onBlurCapture={onBlur} ref={containerRef} />
      {effectiveHelperText ? <FormHelperText>{effectiveHelperText}</FormHelperText> : null}
      {mapToggle ? mapToggle : null}
      {showMap ? (
        <Box sx={{ mt: 2, width: "100%", height: 260 }}>
          {isLoaded && !loadError ? (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={hasCoords ? 16 : 11}
              onClick={handleMapClick}
              onLoad={(map) => {
                mapRef.current = map;
              }}
              onUnmount={() => {
                mapRef.current = null;
                if (advancedMarkerRef.current) {
                  advancedMarkerRef.current.map = null;
                  advancedMarkerRef.current = null;
                }
              }}
              options={{
                fullscreenControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                mapId: mapId || undefined,
              }}
            >
            </GoogleMap>
          ) : null}
        </Box>
      ) : null}
    </FormControl>
  );
};

const ControlledPlaceAutocompleteV2: React.FC<Props> = (props) => {
  const apiKey = String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "");
  const mapId = String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "");

  if (!apiKey) {
    return (
      <TextField
        fullWidth
        label="Dirección"
        value={props.value?.address ?? ""}
        disabled={Boolean(props.disabled)}
        error={props.error}
        helperText={props.helperText || "No se pudo cargar el autocomplete de Google Places, error de configuración"}
        onBlur={props.onBlur}
      />
    );
  }

  return <ControlledPlaceAutocompleteInner {...props} apiKey={apiKey} mapId={mapId} />;
};

export default ControlledPlaceAutocompleteV2;
